import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import {
  isAvailableNow,
  getNextAvailableStart,
  formatDateTimeForAvailabilityHint,
} from '@/utils/availability'

let lastUnavailableToastAt = 0

/**
 * Which availability object to use for “next available” hint when an item tap is blocked.
 * Mirrors isItemClickable order: category schedule first, then item schedule (not when manually off).
 */
export function availabilityForUnavailableToast(group, item, now) {
  if (!group || !item) return null
  if (group.availability != null && typeof group.availability === 'object' && !isAvailableNow(group.availability, now)) {
    return group.availability
  }
  if (item.is_available === false) return null
  if (item.availability != null && typeof item.availability === 'object' && !isAvailableNow(item.availability, now)) {
    return item.availability
  }
  return null
}

/**
 * Public menu: toast when user taps an unavailable item (category hours, item hours, or manual off).
 */
export function usePublicUnavailableToast() {
  const { t, locale } = useI18n()
  const toastStore = useToastStore()

  /**
   * @param {object} group - menu group with optional availability
   * @param {object} item - menu item
   * @param {import('vue').Ref<Date>|Date} nowRef - reactive now or Date
   */
  function resolveNow(nowRef) {
    return nowRef && typeof nowRef === 'object' && 'value' in nowRef
      ? nowRef.value
      : nowRef instanceof Date
        ? nowRef
        : new Date()
  }

  function showUnavailableToast(group, item, nowRef) {
    const now = resolveNow(nowRef)
    const nowTs = Date.now()
    if (nowTs - lastUnavailableToastAt < 1200) return
    lastUnavailableToastAt = nowTs

    const av = availabilityForUnavailableToast(group, item, now)
    const nextStart = av ? getNextAvailableStart(av, now) : null
    const hint = nextStart ? formatDateTimeForAvailabilityHint(nextStart, locale.value) : null
    const msg = hint ? t('public.notAvailableWithNextTime', { time: hint }) : t('public.notAvailable')
    toastStore.info(msg)
  }

  /** When user selects a category tab that is outside its availability window (tab still opens). */
  function showUnavailableCategoryToast(group, nowRef) {
    const now = resolveNow(nowRef)
    const nowTs = Date.now()
    if (nowTs - lastUnavailableToastAt < 1200) return
    lastUnavailableToastAt = nowTs

    const av = group?.availability
    if (av == null || typeof av !== 'object' || isAvailableNow(av, now)) {
      toastStore.info(t('public.notAvailable'))
      return
    }
    const nextStart = getNextAvailableStart(av, now)
    const hint = nextStart ? formatDateTimeForAvailabilityHint(nextStart, locale.value) : null
    const msg = hint ? t('public.notAvailableWithNextTime', { time: hint }) : t('public.notAvailable')
    toastStore.info(msg)
  }

  return { showUnavailableToast, showUnavailableCategoryToast, availabilityForUnavailableToast }
}
